/**
 * LocalStorage utility
 * Centralized storage operations with error handling
 */

import { logger } from '../infrastructure/logger';

export const storage = {
  get: <T>(key: string, defaultValue: T): T => {
    try {
      const item = localStorage.getItem(key);
      if (item === null || item === undefined) {
        return defaultValue;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      logger.error(`[Storage] Error reading from localStorage key "${key}"`, error);
      return defaultValue;
    }
  },

  set: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      logger.error(`[Storage] Error writing to localStorage key "${key}"`, error);
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.error(`[Storage] Error removing localStorage key "${key}"`, error);
    }
  },

  clear: (): void => {
    try {
      localStorage.clear();
    } catch (error) {
      logger.error('[Storage] Error clearing localStorage', error);
    }
  },

  has: (key: string): boolean => {
    try {
      return localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  },
};

export const session = {
  get: <T>(key: string, defaultValue: T): T => {
    try {
      const item = sessionStorage.getItem(key);
      if (item === null || item === undefined) {
        return defaultValue;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      logger.error(`[Storage] Error reading from sessionStorage key "${key}"`, error);
      return defaultValue;
    }
  },

  set: <T>(key: string, value: T): void => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      logger.error(`[Storage] Error writing to sessionStorage key "${key}"`, error);
    }
  },

  remove: (key: string): void => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      logger.error(`[Storage] Error removing sessionStorage key "${key}"`, error);
    }
  },

  clear: (): void => {
    try {
      sessionStorage.clear();
    } catch (error) {
      logger.error('[Storage] Error clearing sessionStorage', error);
    }
  },

  has: (key: string): boolean => {
    try {
      return sessionStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  },
};

export const typedStorage = {
  cart: {
    get: () => storage.get<Array<{ id: string; quantity: number; product: unknown }>>('alltuc_cart', []),
    set: (items: Array<{ id: string; quantity: number; product: unknown }>) => storage.set('alltuc_cart', items),
    remove: () => storage.remove('alltuc_cart'),
  },

  guest: {
    local: {
      get: () => storage.get<{ name?: string; email?: string; dni?: string; phone?: string } | null>('alltuc_guest', null),
      set: (data: { name?: string; email?: string; dni?: string; phone?: string } | null) => storage.set('alltuc_guest', data),
      remove: () => storage.remove('alltuc_guest'),
    },
    session: {
      get: () => session.get<{ name?: string; email?: string; dni?: string; phone?: string } | null>('alltuc_temp_guest_form', null),
      set: (data: { name?: string; email?: string; dni?: string; phone?: string } | null) => session.set('alltuc_temp_guest_form', data),
      remove: () => session.remove('alltuc_temp_guest_form'),
    },
  },

  theme: {
    get: () => storage.get<'light' | 'dark'>('alltuc_theme', 'dark'),
    set: (theme: 'light' | 'dark') => storage.set('alltuc_theme', theme),
  },

  onboardingSeen: {
    get: () => storage.get<boolean>('alltuc_onboarding_seen', false),
    set: (seen: boolean) => storage.set('alltuc_onboarding_seen', seen),
  },

  lastUserEmail: {
    get: () => storage.get<string>('alltuc_last_user_email', ''),
    set: (email: string) => storage.set('alltuc_last_user_email', email),
  },

  guestOrderIds: {
    get: () => storage.get<string[]>('alltuc_guest_order_ids', []),
    set: (orderIds: string[]) => storage.set('alltuc_guest_order_ids', orderIds),
    remove: () => storage.remove('alltuc_guest_order_ids'),
  },

  searchHistory: {
    get: () => storage.get<string[]>('alltuc_search_history', []),
    set: (history: string[]) => storage.set('alltuc_search_history', history),
    remove: () => storage.remove('alltuc_search_history'),
  },
};
